import { useQuery } from "react-query";
import {
	fetchAdmins,
	fetchMe,
	fetchUser,
	fetchUserCenters,
	fetchUsers,
	fetchUserStats,
} from "../queries/user.queries";
import type { PaginatedFilter } from "../types/axios.types";

export const useConnectedUser = () => {
	const query = useQuery(["me"], () => {
		try {
			return fetchMe();
		} catch (err) {
			return null;
		}
	});
	return query?.data;
};

export const useUser = (id: number | string | undefined) => {
	return useQuery(["user", id], () => {
		try {
			return fetchUser(id);
		} catch (err) {
			return null;
		}
	});
};

export const useUserStats = (id: number | string | undefined) => {
	return useQuery(["user-stats", id], () => {
		try {
			return fetchUserStats(id);
		} catch (err) {
			return null;
		}
	});
};

export const useUsers = (
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
) => {
	const query = useQuery(
		["users", page, size, search, filters, sortBy],
		() => {
			try {
				return fetchUsers(page, size, search, filters, sortBy);
			} catch (err) {
				return null;
			}
		},
		{ keepPreviousData: true },
	);
	return query?.data;
};

export const useUsersPaginated = (prefilters?: PaginatedFilter) => {
	return (
		page?: number,
		size?: number,
		search?: string,
		filters?: PaginatedFilter,
		sortBy?: string[],
	) => {
		const f = { ...prefilters, ...filters };
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const query = useQuery(
			["user-centers-paginated", page, size, search, f, sortBy],
			() => {
				try {
					return fetchUsers(page, size, search, f, sortBy);
				} catch (err) {
					return null;
				}
			},
			{ keepPreviousData: true },
		);
		return query?.data;
	};
};

export const useAskForRegistrationUsers = (
	page?: number,
	size?: number,
	search?: string,
	filters?: PaginatedFilter,
	sortBy?: string[],
) => {
	const query = useQuery(
		["users-registration", page, size, search, filters, sortBy],
		() => {
			try {
				return fetchUsers(
					page,
					size,
					search,
					{ ...filters, statut: "deactivated" },
					sortBy,
				);
			} catch (err) {
				return null;
			}
		},
		{ keepPreviousData: true },
	);
	return query?.data;
};

export const useAdmins = () => {
	return useQuery(["admins"], () => {
		try {
			return fetchAdmins();
		} catch (err) {
			return null;
		}
	});
};

export const useUserCentersPaginated = ({
	id,
}: {
	id: number | string | undefined;
}) => {
	return (
		page?: number,
		size?: number,
		search?: string,
		filters?: PaginatedFilter,
		sortBy?: string[],
	) => {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const query = useQuery(
			["user-centers-paginated", id, page, size, search, filters, sortBy],
			() => {
				try {
					return fetchUserCenters(id, page, size, search, filters, sortBy);
				} catch (err) {
					return null;
				}
			},
			{ keepPreviousData: true },
		);
		return query?.data;
	};
};
